import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../services/api";

export interface PortalSubscription {
  id: string;
  productName: string;
  productId?: string;
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
  commitmentEndDate?: string;
  price: {
    amount: number;
    currency: string;
    interval?: string;
    intervalCount?: number;
  };
}

export interface PortalPaymentMethod {
  id: string;
  type: string;
  isDefault: boolean;
  card?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
}

export interface PortalData {
  customer: {
    id: string;
    email?: string;
    name?: string;
    photo?: string;
  };
  business: {
    name?: string;
    logo?: string;
    brandColor?: string;
    supportEmail?: string;
  };
  subscriptions: PortalSubscription[];
  paymentMethods: PortalPaymentMethod[];
  stripeAccountId: string;
}

export interface PortalInvoice {
  id: string;
  number?: string;
  status: string;
  currency: string;
  amountDue: number;
  amountPaid: number;
  total: number;
  createdOn: string;
  paidAt?: string;
  dueDate?: string;
  invoicePdf?: string;
  hostedInvoiceUrl?: string;
  description?: string;
}

export function useVerifyPortalToken(
  stripeCustomerId: string | undefined,
  stripeAccountId: string | undefined,
  token: string | undefined,
) {
  return useQuery({
    queryKey: portalKeys.verifyToken(
      stripeCustomerId!,
      stripeAccountId!,
      token!,
    ),
    queryFn: async () => {
      const response = await api.post<{ valid: boolean }>(
        `/portal/${stripeCustomerId}/verify-token`,
        {
          stripeAccountId,
          token,
        },
      );
      return response.data;
    },
    enabled: !!stripeCustomerId && !!stripeAccountId && !!token,
    retry: false,
  });
}

export function useRequestPortalAccess() {
  return useMutation({
    mutationFn: async ({
      stripeCustomerId,
      stripeAccountId,
    }: {
      stripeCustomerId: string;
      stripeAccountId: string;
    }) => {
      const response = await api.post<{ success: boolean; email: string }>(
        `/portal/${stripeCustomerId}/request-access`,
        {
          stripeAccountId,
        },
      );
      return response.data;
    },
  });
}

export function usePortalData(
  stripeCustomerId: string | undefined,
  stripeAccountId: string | undefined,
  token: string | undefined,
) {
  return useQuery({
    queryKey: portalKeys.data(stripeCustomerId!, stripeAccountId!),
    queryFn: async () => {
      // Send the access token via the Authorization header so it never appears
      // in the URL (access logs / referrers / browser history).
      const response = await api.get<PortalData>(
        `/portal/${stripeCustomerId}?stripeAccountId=${stripeAccountId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    },
    enabled: !!stripeCustomerId && !!stripeAccountId && !!token,
  });
}

export function usePortalInvoices(
  stripeCustomerId: string | undefined,
  stripeAccountId: string | undefined,
  token: string | undefined,
) {
  return useQuery({
    queryKey: portalKeys.invoices(stripeCustomerId!, stripeAccountId!),
    queryFn: async () => {
      const response = await api.get<{ invoices: PortalInvoice[] }>(
        `/portal/${stripeCustomerId}/invoices?stripeAccountId=${stripeAccountId}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    },
    enabled: !!stripeCustomerId && !!stripeAccountId && !!token,
  });
}

export function useCreateSetupIntent() {
  return useMutation({
    mutationFn: async ({
      stripeCustomerId,
      stripeAccountId,
      token,
    }: {
      stripeCustomerId: string;
      stripeAccountId: string;
      token: string;
    }) => {
      const response = await api.post<{
        clientSecret: string;
        setupIntentId: string;
      }>(`/portal/${stripeCustomerId}/setup-intent`, {
        stripeAccountId,
        token,
      });
      return response.data;
    },
  });
}

export function useSetDefaultPaymentMethod(
  stripeCustomerId: string | undefined,
  stripeAccountId: string | undefined,
  token: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const response = await api.post<{ success: boolean }>(
        `/portal/${stripeCustomerId}/default-payment-method`,
        { stripeAccountId, paymentMethodId, token },
      );
      return response.data;
    },
    onSuccess: () => {
      if (stripeCustomerId && stripeAccountId) {
        queryClient.invalidateQueries({
          queryKey: portalKeys.data(stripeCustomerId, stripeAccountId),
        });
      }
    },
  });
}

export function useCancelSubscription(
  stripeCustomerId: string | undefined,
  stripeAccountId: string | undefined,
  token: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const response = await api.post<{
        success: boolean;
        cancelAt?: string;
        status: string;
      }>(`/portal/${stripeCustomerId}/subscriptions/${subscriptionId}/cancel`, {
        stripeAccountId,
        cancelImmediately: false,
        token,
      });
      return response.data;
    },
    onSuccess: () => {
      if (stripeCustomerId && stripeAccountId) {
        queryClient.invalidateQueries({
          queryKey: portalKeys.data(stripeCustomerId, stripeAccountId),
        });
      }
    },
  });
}

export const portalKeys = {
  data: (customerId: string, accountId: string) =>
    ["portal", "data", customerId, accountId] as const,
  invoices: (customerId: string, accountId: string) =>
    ["portal", "invoices", customerId, accountId] as const,
  verifyToken: (customerId: string, accountId: string, token: string) =>
    ["portal", "verify", customerId, accountId, token] as const,
};
